import { __decorate } from "tslib";
import Vue from 'vue';
import Component from 'vue-class-component';
import treeSelect from '@blade/common/components/tree-multiple-select/index.vue';
import ImgChecker from '@blade/common/components/background-management-img-checker/index.vue';
import { isEmpty, map, debounce, snakeCase, camelCase, startsWith, groupBy } from 'lodash';
import { Ref, Watch } from 'vue-property-decorator';
import { Scene } from '@blade-sxp/background-management/interfaces/index';
import { addDeviceBG, fetchLastData, getEquipmentList, getSceneRules, updateDeviceBG } from '@blade-sxp/background-management/api/index';
import typo from '@blade-sxp/background-management/lang/index';
let default_1 = class default_1 extends Vue {
    loading = false;
    form = {
        name: '',
        sceneType: Scene.PASS,
        displayResultType: '',
        displayWord: '',
        backgroundImage: '',
        logoImage: '',
        selectedList: [],
        birthdayDisplay: null,
        imageId: '',
    };
    ruleMap = {};
    formRef;
    get currentRule() {
        return this.ruleMap[this.form.sceneType] || {};
    }
    get birthdayEnable() {
        return this.currentRule[this.form.displayResultType]?.birthdayEnable;
    }
    resetBirthday() {
        if (!this.birthdayEnable) {
            this.form.birthdayDisplay = false;
        }
        else {
            this.form.birthdayDisplay = null;
        }
    }
    passTreeData = [];
    equimentMap = {};
    onBackgroundImageChange() {
        this.$nextTick(() => this.formRef.validateField('backgroundImage'));
    }
    submitDebounced = debounce(this.onSubmit, 1000, { leading: true });
    inputRule = { required: true, message: this.$t('common.pleaseInput').toString(), trigger: ['change', 'blur'] };
    selectRule = { required: true, message: this.$t('common.pleaseSelect').toString(), trigger: ['change', 'blur'] };
    get rules() {
        return {
            name: [this.inputRule],
            birthdayDisplay: [this.selectRule],
            displayResultType: [this.selectRule],
            backgroundImage: [this.selectRule],
            displayWord: [{ validator: this.validateTypo, trigger: ['change', 'blur', 'submit'] }],
            selectedList: [this.selectRule, { validator: this.validateEquipment, trigger: 'submit' }],
        };
    }
    validateTypo(_, value, callback) {
        if (/\n/.test(value)) {
            callback(new Error(this.$t('noWrap').toString()));
        }
        else {
            callback();
        }
    }
    validateEquipment(_, value, callback) {
        if (this.form.selectedList.length > 0) {
            callback();
        }
        else {
            callback(new Error(this.$t('common.pleaseSelect').toString()));
        }
    }
    onSceneChange() {
        this.filterEquipmentMap();
        this.resetBirthday();
    }
    onCancel() {
        this.$router.back();
    }
    async mounted() {
        this.loading = true;
        await this.getEquipment();
        Promise.all([this.fetchRules(), this.fill()]).then(() => {
            this.loading = false;
        });
    }
    fetchRules() {
        return getSceneRules().then((data) => {
            this.ruleMap = JSON.parse(data);
        });
    }
    getEquipment() {
        return getEquipmentList().then((data) => {
            const { tree } = data || {};
            this.equimentMap = groupBy(tree, 'sceneType');
            this.filterEquipmentMap();
        });
    }
    filterEquipmentMap(sceneType) {
        this.form.selectedList = [];
        const currentList = this.equimentMap[sceneType || this.form.sceneType];
        this.passTreeData = this.mapTreeAndSelectAll(currentList);
    }
    async fill() {
        const id = this.$route.query?.id;
        if (id) {
            this.$route.meta.title = 'backgroundManagementDetail';
            fetchLastData(id).then((data) => {
                const { backgroundImagePath, birthdayDisplay, deviceGroupVOS, displayResultType, displayWord, imageId, logoImagePath, name, sceneType, } = data;
                this.filterEquipmentMap(sceneType);
                this.form = {
                    ...this.form,
                    name,
                    backgroundImage: backgroundImagePath,
                    logoImage: logoImagePath,
                    displayWord,
                    birthdayDisplay,
                    displayResultType: camelCase(displayResultType),
                    sceneType,
                    selectedList: map(deviceGroupVOS, ({ id, name }) => ({ id, key: id, label: name })),
                };
                this.form.imageId = imageId;
            });
        }
        else {
            this.$route.meta.title = 'backgroundManagementCreat';
        }
    }
    async onSubmit() {
        try {
            await this.formRef.validate();
            this.loading = true;
            const { imageId, backgroundImage, logoImage, selectedList, displayResultType, birthdayDisplay, ...rest } = this.form;
            const apiCall = this.form.imageId ? updateDeviceBG : addDeviceBG;
            apiCall({
                ...rest,
                imageId: imageId || undefined,
                deviceGroupIds: map(selectedList, 'id'),
                displayResultType: snakeCase(displayResultType).toUpperCase(),
                birthdayDisplay: !!birthdayDisplay,
                backgroundImage: startsWith(backgroundImage, '/') ? undefined : backgroundImage,
                logoImage: startsWith(logoImage, '/') ? undefined : logoImage,
            })
                .then(() => {
                this.$elmessage.success(this.$t('common.toolSuccess').toString());
                this.$router.back();
            })
                .finally(() => {
                this.loading = false;
            });
        }
        catch (e) { }
    }
    mapTreeAndSelectAll(originTree) {
        return isEmpty(originTree)
            ? []
            : map(originTree, ({ id, children, name, nodeType }) => ({
                id,
                key: id,
                label: name,
                children: this.mapTreeAndSelectAll(children),
            }));
    }
};
__decorate([
    Ref()
], default_1.prototype, "formRef", void 0);
__decorate([
    Watch('form.backgroundImage')
], default_1.prototype, "onBackgroundImageChange", null);
default_1 = __decorate([
    Component({
        name: 'detail',
        components: { treeSelect, ImgChecker },
        i18n: {
            messages: {
                en: {
                    name: 'Image Name',
                    scene: 'Sense',
                    equipment: 'Associated Device Group',
                    typo: 'Input Text',
                    resultType: 'Status',
                    bg: 'Upload Background  Image',
                    birthDay: 'Have Birthday Status',
                    faceMatchSuccess: 'Recognized successfully',
                    faceMatchFail: 'Recognition failure',
                    paymentAccept: 'Payment successfully',
                    paymentDecline: 'Payment failure',
                    ticketCompareFail: 'Ticket comparison failure',
                    logo: 'Insert logo',
                    noWrap: 'Cannot wrap words',
                    submit: 'Submit',
                    message: '* Applies to all devices in the selected device group',
                    pass: 'Pass',
                },
                zh: {
                    name: '背景图名称',
                    scene: '使用场景',
                    equipment: '关联设备组',
                    typo: '插入文本框',
                    resultType: '选择结果',
                    bg: '上传底图',
                    birthDay: '是否为生日场景',
                    faceMatchSuccess: '识别成功',
                    faceMatchFail: '识别失败',
                    paymentAccept: '支付成功',
                    paymentDecline: '支付失败',
                    ticketCompareFail: '票务识别失败',
                    logo: '插入logo',
                    noWrap: '不能换行',
                    submit: '下发图片',
                    message: '* 对所选设备组中的所有设备均生效',
                    pass: '通行',
                },
            },
            sharedMessages: typo.messages,
        },
    })
], default_1);
export default default_1;
